import { createHttpClient } from '@adapters/HttpClient';
import type { HttpClient } from '@adapters/HttpClient/types';
import { apiClientWH } from '@shared/services/ApiClient';

// ========================================
// INTERFACES - Preview (misma forma que GET /orders/{id})
// ========================================

export interface AmazonFbmPreviewCustomerAddress {
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  province?: string | null;
  zip?: string | null;
  country_code?: string | null;
}

export interface AmazonFbmPreviewCustomer {
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: AmazonFbmPreviewCustomerAddress | null;
}

export interface AmazonFbmPreviewLineProduct {
  id: number;
  name: string;
  code?: string | null;
  image_hash?: string | null;
}

export interface AmazonFbmPreviewLinePack {
  id?: number;
  title?: string;
  pack_version_id?: number | null;
}

/** Línea de pedido en preview (alineada con GET /orders/{id}) */
export interface AmazonFbmPreviewLine {
  id: number | null;
  product_id?: number | null;
  pack_id?: number | null;
  name: string;
  amount?: number;
  external_product_id?: string | null;
  external_variant_id: string | null;
  qty: number;
  product?: AmazonFbmPreviewLineProduct | null;
  pack?: AmazonFbmPreviewLinePack | null;
}

export interface AmazonFbmPreviewOrderShop {
  id?: number;
  name?: string;
  channel_id?: number;
}

/** Pedido en preview (hasta 10; mismos campos que GET /orders/{id}, ids null hasta importar) */
export interface AmazonFbmPreviewOrder {
  id: number | null;
  ref: string | null;
  external_id?: string | null;
  shop_id?: number;
  shop?: AmazonFbmPreviewOrderShop | null;
  customer?: AmazonFbmPreviewCustomer | null;
  order_created_at?: string | null;
  order_paid_at?: string | null;
  total?: number;
  currency?: string | null;
  external_payment_method?: string | null;
  lines?: AmazonFbmPreviewLine[];
  errors?: string[];
  errors_api?: unknown[];
}

/** Errores globales del archivo: strings o { line, message } */
export type AmazonFbmPreviewRootErrorItem = string | { line: number; message: string };

export interface AmazonFbmPreviewResponse {
  total_orders: number;
  total_lines: number;
  valid_orders: number;
  orders_with_errors: number;
  preview_orders: AmazonFbmPreviewOrder[];
  errors: AmazonFbmPreviewRootErrorItem[];
}

// ========================================
// INTERFACES - Import
// ========================================

export interface AmazonFbmImportResponse {
  success_count: number;
  error_count: number;
  error_file: string | false;
}

// ========================================
// ADAPTER
// ========================================

const ALLOWED_EXTENSIONS = ['.tsv', '.txt'];

export class AmazonFbmImportApiAdapter {
  private httpClient: HttpClient;

  constructor(httpClient?: HttpClient) {
    this.httpClient = httpClient ?? createHttpClient(apiClientWH);
  }

  private validateFile(file: File): void {
    const extension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      throw new Error('Solo se permiten archivos TSV o TXT');
    }
  }

  /**
   * Previsualizar importación sin crear pedidos
   * POST /orders/import/amazon-fbm/preview
   * @param shopId - Recomendado: mapeo SKU→producto y validación de tienda Amazon FBM
   */
  async preview(file: File, shopId?: number): Promise<AmazonFbmPreviewResponse> {
    this.validateFile(file);
    const formData = new FormData();
    formData.append('file', file);
    if (shopId != null) {
      formData.append('shop_id', shopId.toString());
    }

    return this.httpClient.post<AmazonFbmPreviewResponse>('/orders/import/amazon-fbm/preview', formData);
  }

  /**
   * Importar pedidos desde archivo
   * POST /orders/import/amazon-fbm
   */
  async import(file: File, shopId: number): Promise<AmazonFbmImportResponse> {
    this.validateFile(file);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('shop_id', shopId.toString());

    return this.httpClient.post<AmazonFbmImportResponse>('/orders/import/amazon-fbm', formData);
  }
}
